import {
  AbutmentType,
  RetentionType,
  OcclusalContactTightness,
  ProximalContactTightness,
  OcclusalStaining,
  NoOcclusalClearance,
  InterproximalContactDesign,
  AnteriorPosteriorPonticDesign,
  ItemType,
  ImprintType,
  RemovableItemOrderType,
  CarrierType,
  PartialAndFullDentureProsthesisShadeType,
  OrderStatus,
} from "./enums";
import { TranslationOptions } from "./interfaces";

export const UNACTIVATED_ORDER_STATUS: OrderStatus[] = [
  OrderStatus.DRAFT,
  OrderStatus.NEED_TO_BE_PROCESSED,
  OrderStatus.ON_HOLD,
  OrderStatus.CONFIRMED,
  OrderStatus.CONFIRMED,
  OrderStatus.DELIVERED,
];

export const ACTIVATED_ORDER_STATUS: OrderStatus[] = [
  OrderStatus.PRODUCING_IN_TRANSIT,
  OrderStatus.WAITING_FOR_PRODUCTION,
  OrderStatus.OPS_SHIPPING_TODAY,
  OrderStatus.PRODUCING,
  OrderStatus.SHIPPED,
];

export const PHONE_NUMBER_DEFAULT_VALUE = "NOT_PROVIDED";

export const STYLE = {
  primary: "#4362D0",
  border: "#ebf0f6",
  secondary: "#2b8a3e",
};

export const TranslatedItemPreferencesName: {
  OCCLUSAL_STAINING: TranslationOptions;
  OCCLUSAL_CONTACT_TIGHTNESS: TranslationOptions;
  PROXIMAL_CONTACT_TIGHTNESS: TranslationOptions;
  NO_OCCLUSAL_CLEARANCE: TranslationOptions;
  INTERPROXIMAL_CONTACT_DESIGN: TranslationOptions;
  ANTERIOR_POSTERIOR_PONTIC_DESIGN: TranslationOptions;
} = {
  OCCLUSAL_STAINING: {
    EN: "Occlusal staining",
    FR: "Coloration occlusale",
  },
  OCCLUSAL_CONTACT_TIGHTNESS: {
    EN: "Occlusal contact tightness",
    FR: "Occlusion",
  },
  PROXIMAL_CONTACT_TIGHTNESS: {
    EN: "Proximal contact tightness",
    FR: "Contact proximal",
  },
  NO_OCCLUSAL_CLEARANCE: {
    EN: "No occlusal clearance",
    FR: "Que doit faire Jasper en cas d'espace occlusal insuffisant ?",
  },
  INTERPROXIMAL_CONTACT_DESIGN: {
    EN: "Interproximal contact design",
    FR: "Étendue du point de contact inter dentaire",
  },
  ANTERIOR_POSTERIOR_PONTIC_DESIGN: {
    EN: "Anterior Posterior Pontic Design",
    FR: "Design du pontique antérieur et postérieur",
  },
};

export const YES_OR_NO_CHOICE = [
  {
    value: true,
    label: "Yes",
  },
  {
    value: false,
    label: "No",
  },
];

export const NEW_PAP_WORKFLOW_ACTIVATION_DATE = new Date(
  "2025-09-24T20:00:00.000Z"
);

export const TOGGLE_FEATURE = {
  PICKUP_CARRIER_INTEGRATION_FOR_DENTIST: (email: string) =>
    import.meta.env.MODE === "development" ||
    email === "stanley.serbin@jasperdental.fr" ||
    email === "gabriel.locher@jasperdental.fr",
  SHIPPING_FROM_WEBAPP: (email: string) => true,
  DATA_PAGE: (email: string) =>
    import.meta.env.MODE === "development" ||
    email === "stanley.serbin@jasperdental.fr" ||
    email === "gabriel.locher@jasperdental.fr",
  INTEGRATED_PICKUP_CARRIERS: (email: string): CarrierType[] => [
    CarrierType.FEDEX_TNT,
    CarrierType.CIBLEX,
    CarrierType.BIP,
    CarrierType.STERNE,
  ],
  INTEGRATED_DELIVERY_CARRIERS: (email: string): CarrierType[] => [
    CarrierType.FEDEX_TNT,
    CarrierType.CIBLEX,
    CarrierType.BIP,
    CarrierType.STERNE,
    CarrierType.CHRONOPOST,
  ],
};

export const AbutmentTypeChoices = [
  { value: AbutmentType.STANDARD, label: "Standard" },
  { value: AbutmentType.CUSTOM, label: "Personnalisé" },
];

export const StandardAbutmentTypeChoices = [
  { value: AbutmentType.STANDARD, label: "Pilier original - fabriquant" },
  { value: AbutmentType.STANDARD_GENERIC, label: "Pilier standard générique" },
];

export const RetentionTypeChoices = [
  { value: RetentionType.CEMENT_RETAINED, label: "Scellé" },
  { value: RetentionType.SCREW_RETAINED, label: "Transvissé" },
];

export const OcclusalContactTightnessLabels: {
  value: string;
  label: string;
}[] = [
  {
    value: OcclusalContactTightness.HEAVY,
    label: "Forte",
  },
  {
    value: OcclusalContactTightness.MEDIUM,
    label: "Moyenne - par défaut",
  },
  {
    value: OcclusalContactTightness.LIGHT,
    label: "Légère",
  },
];

export const ProximalContactTightnessLabels: {
  value: string;
  label: string;
}[] = [
  {
    value: ProximalContactTightness.HEAVY,
    label: "Fort",
  },
  {
    value: ProximalContactTightness.MEDIUM,
    label: "Moyen - par défaut",
  },
  {
    value: ProximalContactTightness.LIGHT,
    label: "Léger",
  },
  {
    value: ProximalContactTightness.NONE,
    label: "Aucun",
  },
];

export const OcclusalStainingLabels: { value: string; label: string }[] = [
  {
    value: OcclusalStaining.DARK,
    label: "Foncé",
  },
  {
    value: OcclusalStaining.MEDIUM,
    label: "Moyen",
  },
  {
    value: OcclusalStaining.LIGHT,
    label: "Clair",
  },
  {
    value: OcclusalStaining.NONE,
    label: "Aucun",
  },
];

export const NoOcclusalClearanceLabels: { value: string; label: string }[] = [
  {
    value: NoOcclusalClearance.ASK_DENTIST,
    label: "Consulter le praticien",
  },
  {
    value: NoOcclusalClearance.KEEP_MANUFACTURING,
    label: "Poursuivre la fabrication",
  },
];

export const InterproximalContactDesignLabels: {
  value: string;
  label: string;
}[] = [
  {
    value: InterproximalContactDesign.NORMAL_CONTACT_POINT,
    label: "Point de contact",
  },
  {
    value: InterproximalContactDesign.WIDE_CONTACT_SURFACE,
    label: "Surface de contact",
  },
];

export const AnteriorPosteriorPonticDesignLabels: {
  value: string;
  label: string;
}[] = [
  {
    value: AnteriorPosteriorPonticDesign.MODIFIED_RIDGE_LAP,
    label: "Pontique crestal modifié",
  },
  {
    value: AnteriorPosteriorPonticDesign.FULL_RIDGE_LAP,
    label: "Complet",
  },
];

export const SPLINT_TYPE = [
  {
    value: ItemType.BLEACHING_TRAY,
    label: "Blanchiment",
  },
  {
    value: ItemType.NIGHT_GUARD,
    label: "Bruxisme",
  },
  {
    value: ItemType.RETAINER,
    label: "Contention",
  },
];

export const SPLINT_HARDNESS_CHOICE = [
  {
    value: "NIGHT_GUARD_SOFT_MATERIAL",
    label: "Souple",
  },
  {
    value: "NIGHT_GUARD_HARD_MATERIAL",
    label: "Dure",
  },
  {
    value: "NIGHT_GUARD_MEDIUM_MATERIAL",
    label: "Semi-rigide",
  },
];

export const UpperArchTeeth = [
  11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25, 26, 27, 28,
];

export const LowerArchTeeth = [
  31, 32, 33, 34, 35, 36, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48,
];

export const FullDentureTeethShape = [
  {
    label: "Carrées",
    value: "SQUARE",
  },
  {
    label: "Arrondies",
    value: "ROUNDED",
  },
  {
    label: "Triangulaires",
    value: "TRIANGULAR",
  },
];

export const NewFullDentureStepChoices = [
  {
    value: 1,
    label: "Étape 1: Commande de la cire d'occlusion",
  },
  {
    value: 2,
    label: "Étape 2: Commande de la PAC définitive",
  },
];

export const FullDentureTypeChoices = [
  {
    value: ItemType.FULL_DENTURE,
    label: "PAC Standard",
  },
  {
    value: ItemType.IMMEDIATE_DENTURE,
    label: "PAC Immédiate",
  },
];

export const ImprintTypeChoices = [
  {
    value: ImprintType.EDENTULOUS_ARCH_RDV1,
    label: "Arcade(s) édentée(s) - RDV 1",
  },
  {
    value: ImprintType.PRE_EXISTING_DENTURE_RDV1,
    label: "Prothèse(s) pré-existante(s) du patient - RDV 1",
  },
  {
    value: ImprintType.WAX_BITE_RDV2,
    label: "Cire d'occlusion - RDV 2",
  },
];

export const FullDentureOrderTypeChoices = [
  {
    value: RemovableItemOrderType.WAX_BITE,
    label: "Cire(s) d'occlusion",
  },
  {
    value: RemovableItemOrderType.TRY_IN,
    label: "Essayage avec montage des dents en cire",
  },
  {
    value: RemovableItemOrderType.FINAL,
    label: "Prothèse amovible complète (Finition Directe)",
  },
];

export const PartialDentureOrderTypeChoices = [
  {
    value: RemovableItemOrderType.WAX_BITE,
    label: "Cire(s) d'occlusion",
  },
  {
    value: RemovableItemOrderType.TRY_IN,
    label: "Essayage avec montage des dents en cire",
  },
  {
    value: RemovableItemOrderType.FINAL,
    label: "Prothèse amovible partielle (Finition Directe)",
  },
];

export const shadeTypeChoices = [
  {
    value: PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
    label: "VITA CLASSICAL",
  },
  {
    value: PartialAndFullDentureProsthesisShadeType.VITA_3D_MASTER,
    label: "VITA 3D MASTER",
  },
  {
    value: PartialAndFullDentureProsthesisShadeType.CHROMASCOP,
    label: "CHROMASCOP",
  },
  {
    value: PartialAndFullDentureProsthesisShadeType.SR_VIVODENT_SPE,
    label: "SR VIVODENT SPE",
  },
];

export const JASPER_BOT_USER_GROUP_ID = "e531e4ae-32e0-4224-93b5-b3c6de154fa0";
